import { Controller } from "@hotwired/stimulus"

/**
 * Inline Select Controller
 *
 * Persists an app-select's choice as soon as it changes, so a field like a user's
 * role needs no surrounding form. A rejected change reverts the select to the
 * value the server still holds, rather than leaving the UI lying about it.
 *
 * Values:
 *   - url: where to PATCH
 *   - field: the attribute being set
 *   - scope: the param scope the field nests under (defaults to "user")
 */
export default class extends Controller {
  static targets = ["error"]
  static values = {
    url: String,
    field: String,
    scope: { type: String, default: "user" }
  }

  connect() {
    this.previousValue = this.hiddenInput?.value
    this.reverting = false
    this.boundSave = this.save.bind(this)
    this.element.addEventListener("app-select:change", this.boundSave)
  }

  disconnect() {
    this.element.removeEventListener("app-select:change", this.boundSave)
  }

  async save(event) {
    if (this.reverting) return

    const value = event.detail?.value
    if (!value || !this.hasUrlValue || !this.hasFieldValue || value === this.previousValue) return

    this.clearError()
    this.setDisabled(true)

    const formData = new FormData()
    formData.append(`${this.scopeValue}[${this.fieldValue}]`, value)
    formData.append("_method", "PATCH")

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
          "Accept": "text/vnd.turbo-stream.html"
        },
        credentials: "same-origin"
      })

      const body = await response.text()

      if (!response.ok) {
        this.revert()
        this.showError(this.errorMessageFrom(body) || `Could not update ${this.fieldLabel()}.`)
        return
      }

      this.previousValue = value
      Turbo.renderStreamMessage(body)
    } catch (error) {
      console.error(`Error updating ${this.fieldValue}:`, error)
      this.revert()
      this.showError("Something went wrong. Please try again.")
    } finally {
      this.setDisabled(false)
    }
  }

  revert() {
    if (!this.previousValue) return

    const option = this.element.querySelector(
      `[data-app-select-target="option"][data-value="${this.previousValue}"]`
    )
    if (!option) return

    this.reverting = true
    option.click()
    this.reverting = false
  }

  setDisabled(disabled) {
    const button = this.element.querySelector(".app-select-button")
    if (button) button.disabled = disabled
  }

  fieldLabel() {
    return this.fieldValue.replace(/_/g, " ")
  }

  get hiddenInput() {
    return this.element.querySelector(".select-hidden-input-value")
  }

  errorMessageFrom(body) {
    try {
      const data = JSON.parse(body)
      if (data?.errors?.length) return data.errors.join(", ")
    } catch {
      // Not a JSON payload — fall back to the generic message.
    }
    return null
  }

  showError(message) {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("d-none")
  }

  clearError() {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("d-none")
  }
}
